import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FedWriteOrigin, InstanceActivityEvent, InstanceActivityKind } from "@keydials/api-client";
import { useCapabilities, useWorkspaceServices } from "../context";
import { DEFAULT_SEGMENT_NOUN, type SegmentNoun } from "../vocabulary";
import { Icon, type IconName } from "../components/Icon";
import { timeAgo } from "../lib/ui";

/**
 * Who did it, for the bold name at the front of a timeline line.
 *
 * Every system source needs its own answer here. Before KEY-360 this branched only on auto-revert, so a
 * propagated write — and later a scheduled reset — rendered as an **empty bold name**: the actor is the
 * all-zero guid, and the server resolves that to an empty email. A blank in an audit trail is worse than a
 * vague one, because it reads as a missing record rather than a system action.
 *
 * A fed write names the ORGANISATION and nothing finer: the consent record binds organisations, and
 * carrying a named individual across a tenant boundary is a decision this product has taken the other way.
 */
export function activityActor(e: { source?: string; fedFrom?: FedWriteOrigin | null; actorEmail?: string }): string {
  if (e.fedFrom) return e.fedFrom.organisationName;
  switch (e.source) {
    case "AutoRevert":
      return "Auto-revert";
    case "ScheduledReset":
      return "Scheduled reset";
    // KEY-LADDER: the due-date ladder moving a dated node to the rung its date has reached.
    case "DateLadder":
      return "Due date";
    // KEY-NEXTROUND: a field emptied because the node's review came round again.
    case "NextRound":
      return "Next round";
    case "Propagated":
      return "A linked dial";
    // A version upgrade moving a status onto a key the new version has (the stranded-status remap).
    case "Upgrade":
      return "Version upgrade";
    // The two field-only sources (KEY-208, KEY-233): a bulk write with no person behind the individual row.
    case "Import":
      return "An import";
    case "ItemImport":
      return "An item import";
    // The one phase-2 (KEY-489) system source: the guest-expiry sweep, which notices a moment nobody chose.
    case "System":
      return "The system";
    default:
      // A real person, unless their account has gone since — in which case say so rather than show a gap.
      return e.actorEmail || "Someone";
  }
}

/**
 * Which icon a row carries. The level it happened at, mostly — the same three shapes the file browser uses
 * for the same three levels (layers/target/check), so a section reads as a section wherever you meet one —
 * except where the SOURCE is the more useful fact: a system write nobody made says so with a clock, and a
 * field change is a pencil rather than a fourth level, because it happens at all three.
 */
function iconFor(e: InstanceActivityEvent, noun: SegmentNoun): { name: IconName; tint: string; title: string } {
  if (e.source === "AutomationSkipped") {
    return { name: "ban", tint: "var(--text-faint)", title: "Automatic change held back by a status gate" };
  }
  if (e.source === "AutoRevert" || e.source === "ScheduledReset" || e.source === "DateLadder" || e.source === "System") {
    return { name: "clock", tint: "var(--st-overdue)", title: "Automatic change" };
  }
  if (e.source === "Upgrade") {
    return { name: "clock", tint: "var(--text-muted)", title: "Changed by a version upgrade" };
  }
  if (e.fedFrom) {
    return { name: "share", tint: "var(--text-muted)", title: "Change fed in by another organisation" };
  }

  const byKind: Record<InstanceActivityKind, { name: IconName; title: string }> = {
    SectionStatus: { name: "layers", title: "Section status" },
    SegmentStatus: { name: "target", title: `${noun.One} status` },
    ItemStatus: { name: "check", title: "Item status" },
    Attribute: { name: "pencil", title: "Field" },
    FileUploaded: { name: "upload", title: "File uploaded" },
    FileDeleted: { name: "trash", title: "File deleted" },
    FileVersioned: { name: "clip", title: "New file version" },
    ItemAdded: { name: "plus", title: "Item added" },
    ItemHidden: { name: "ban", title: "Item hidden" },
    ItemRestored: { name: "check", title: "Item restored" },
    MemberAdded: { name: "users", title: "Member added" },
    MemberRemoved: { name: "logout", title: "Member removed" },
    MemberRoleChanged: { name: "gear", title: "Member role changed" },
    GuestInvited: { name: "send", title: "Guest invited" },
    GuestExpired: { name: "clock", title: "Guest access expired" },
    GuestRevoked: { name: "x", title: "Guest access revoked" },
    StructureEdited: { name: "list", title: "Structure edited" },
    AppointmentAdded: { name: "calendar", title: "Appointment added" },
    AppointmentChanged: { name: "calendar", title: "Appointment changed" },
    AppointmentDeleted: { name: "trash", title: "Appointment deleted" },
  };
  const kind = byKind[e.kind] ?? { name: "activity" as IconName, title: "Change" };
  return { ...kind, tint: "var(--accent)" };
}

/** The rendered trail. Separated from the fetching so the same rows can be shown from anywhere. */
export function ActivityTimeline({
  events,
  showPath = true,
  noun = DEFAULT_SEGMENT_NOUN,
  emptyMessage = "No activity recorded yet.",
}: {
  events: InstanceActivityEvent[];
  showPath?: boolean;
  noun?: SegmentNoun;
  /** Swapped in by the search box (KEY-561) when a non-empty feed has been filtered down to nothing —
   *  "no matches" and "nothing ever happened here" are different facts and shouldn't share one sentence. */
  emptyMessage?: string;
}) {
  if (events.length === 0) {
    return <p style={{ color: "var(--text-faint)", fontSize: 14, margin: 0 }}>{emptyMessage}</p>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {events.map((e, i) => {
        const icon = iconFor(e, noun);
        return (
          <div key={`${e.occurredAtUtc}-${i}`} style={{ display: "flex", gap: 12 }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <span
                title={icon.title}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center", flex: "none",
                  width: 24, height: 24, borderRadius: 99, marginTop: 1,
                  border: "var(--border-w) solid var(--border)", background: "var(--surface-2)", color: icon.tint,
                }}
              >
                <Icon name={icon.name} size={13} />
              </span>
              {i < events.length - 1 && <span style={{ flex: 1, width: 2, background: "var(--border)", marginTop: 4 }} />}
            </div>
            <div style={{ paddingBottom: 2, minWidth: 0 }}>
              <div style={{ fontSize: 14, color: "var(--text)" }}>
                <span style={{ fontWeight: 700 }}>{activityActor(e)}</span> {e.description}
              </div>
              {/* Where it happened. Suppressed at the action point, where every row is about the one node
                  already named at the top of the panel and the path would repeat itself down the list. */}
              {showPath && e.nodePath && (
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 1 }}>{e.nodePath}</div>
              )}
              {/* A foreign write has to say whose, and which of THEIR nodes drove it — the names are
                  carried on the row, so this still reads correctly long after the feed has been stopped. */}
              {e.fedFrom && (
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 1 }}>
                  fed from “{e.fedFrom.dialName}” · {e.fedFrom.nodePath}
                </div>
              )}
              <div style={{ fontSize: 12, color: "var(--text-faint)", marginTop: 1 }}>{timeAgo(e.occurredAtUtc)}</div>
              {e.note && <div style={{ fontSize: 13, color: "var(--text-muted)", fontStyle: "italic", marginTop: 2 }}>“{e.note}”</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * The Activity tab, at whichever level it is rendered (KEY-464 phase 1).
 *
 * One component for all three because the server does the merging and the narrowing: the dial passes no
 * keys, a section passes its own, an action point passes both. Until now this surface existed only on an
 * action point, and only over the two status ledgers the workspace happened to have already loaded — so a
 * section status and every field change were invisible everywhere, though both had been recorded all along.
 *
 * <b>Conversation messages are not here and must not be added</b> (decision 1 / KEY-285): a line saying a
 * restricted message exists undoes the confidentiality the restriction is for.
 */
export function ActivityPanel({
  instanceId,
  sectionKey,
  segmentKey,
  card = true,
  noun = DEFAULT_SEGMENT_NOUN,
}: {
  instanceId: string;
  sectionKey?: string | null;
  segmentKey?: string | null;
  /** Wrap in the panel card. False where the caller already draws one (the action-point tab body). */
  card?: boolean;
  /** This dial's word for an action point (KEY-539). This panel is mounted with keys rather than the
   *  instance detail, so the vocabulary arrives as a prop; omitted means the platform's word. */
  noun?: SegmentNoun;
}) {
  const { api, getAccessToken } = useWorkspaceServices();
  const caps = useCapabilities();
  const [events, setEvents] = useState<InstanceActivityEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Collapsed by default (KEY-561): the toggle is the only thing rendered until opened, so a short feed
  // never carries a search box it doesn't need. `search` is client-side only — see the filter below.
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    const token = getAccessToken();
    if (!token || caps.mode !== "live") {
      return;
    }
    try {
      setEvents(await api.getInstanceActivity(token, instanceId, { sectionKey, segmentKey }));
      setError(null);
    } catch {
      setError("Could not load the activity log.");
    }
  }, [instanceId, sectionKey, segmentKey, caps.mode]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (searchOpen) {
      searchInputRef.current?.focus();
    }
  }, [searchOpen]);

  function toggleSearch() {
    setSearchOpen((open) => !open);
    // Closing clears the query rather than just hiding the box: a filter left active behind a collapsed
    // toggle would silently hide rows with no visible reason why.
    setSearch("");
  }

  /**
   * Filtered client-side, not server-side (KEY-561 decision): `getInstanceActivity` already returns the
   * whole bounded, merged, sorted window in one call with no pagination — unlike `InstanceFilesPanel`,
   * which searches server-side because it only ever holds one page of a much larger result. Narrowing a
   * set already in hand doesn't need a round trip.
   */
  const filteredEvents = useMemo(() => {
    if (!events) {
      return events;
    }
    const q = search.trim().toLowerCase();
    if (!q) {
      return events;
    }
    return events.filter(
      (e) =>
        e.description.toLowerCase().includes(q) ||
        activityActor(e).toLowerCase().includes(q) ||
        (e.nodePath ?? "").toLowerCase().includes(q),
    );
  }, [events, search]);

  const canSearch = caps.mode === "live" && error === null && events !== null;

  const body = caps.mode !== "live" ? (
    // A draft preview has no instance to have a history, so there is nothing to fetch and nothing to show.
    <p style={{ color: "var(--text-faint)", fontSize: 14, margin: 0 }}>Activity isn’t available in preview.</p>
  ) : error ? (
    <p style={{ color: "var(--danger-text, #c11118)", fontSize: 14, margin: 0 }}>{error}</p>
  ) : events === null ? (
    <p style={{ color: "var(--text-faint)", fontSize: 14, margin: 0 }}>Loading activity…</p>
  ) : (
    <ActivityTimeline
      events={filteredEvents ?? []}
      showPath={!segmentKey}
      noun={noun}
      emptyMessage={events.length > 0 ? "No activity matches your search." : "No activity recorded yet."}
    />
  );

  const content = (
    <>
      {canSearch && (
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginBottom: 12 }}>
          {searchOpen && (
            <input
              ref={searchInputRef}
              className="kd-focusable"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search activity…"
              aria-label="Search activity"
              style={{
                font: "inherit", fontSize: 12.5, padding: "5px 9px", borderRadius: "var(--radius-sm)",
                border: "var(--border-w) solid var(--border)", background: "var(--surface)", color: "var(--text)",
                minWidth: 170, flex: "1 1 auto",
              }}
            />
          )}
          <button
            type="button"
            className="kd-focusable"
            aria-pressed={searchOpen}
            aria-label={searchOpen ? "Hide activity search" : "Show activity search"}
            title={searchOpen ? "Hide search" : "Search activity"}
            onClick={toggleSearch}
            style={{
              border: "none", background: "none", cursor: "pointer", padding: 4, flex: "none",
              display: "flex", color: searchOpen ? "var(--accent)" : "var(--text-faint)",
            }}
          >
            <Icon name="search" size={16} />
          </button>
        </div>
      )}
      {body}
    </>
  );

  return card ? <div className="kd-card" style={{ padding: "var(--pad-card)" }}>{content}</div> : content;
}
